import { Grid, InputAdornment } from '@mui/material'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { MdSearch } from 'react-icons/md'
import { z } from 'zod'
import { useGetLocalityNames } from '../../../../api/localities'
import { useGetAllResources } from '../../../../api/task-resource'
import BasicDatePicker from '../../../../components/DateTimePicker'
import { AppForm, FormSelectField, FormTextField } from '../../../../components/FormCore'
import { useCustomerCtx } from '../../../../hooks/context-hooks'
import { useZodForm } from '../../../../hooks/zod-form'
import { APIResultCount } from 'src/constant'

const TasksFilter = ({ onChange, isDisabled = false, startDate, endDate }) => {
  const { customer } = useCustomerCtx()
  const { t } = useTranslation(['common', 'task'])
  const { data } = useGetLocalityNames(customer?.id)
  const { data: resources } = useGetAllResources(customer?.id, 0, APIResultCount.Max)

  const taskFilterSchema = z
    .object({
      locality: z
        .array(
          z.object({
            id: z.number(),
          }),
        )
        .nullable(),
      resource: z
        .array(
          z.object({
            id: z.number(),
          }),
        )
        .nullable(),
      startDate: z.date().optional().nullable(),
      endDate: z.date().optional().nullable(),
      search: z.string().optional().nullable(),
    })
    .refine(
      data =>
        data.endDate == undefined ||
        data.startDate == undefined ||
        (data.endDate != undefined && data.startDate != undefined && data.endDate > data.startDate),
      {
        message: t('endStartDateError', { ns: 'common' }),
        path: ['endDate'],
      },
    )

  const form = useZodForm(taskFilterSchema, {
    defaultValues: { locality: [], resource: [], search: '' },
  })

  useEffect(() => {
    const subscription = form.watch(value => {
      if (form.formState.isValid) {
        onChange(value)
      }
    })
    return () => subscription.unsubscribe()
  }, [form.watch, onChange, form.formState.isValid])

  useEffect(() => {
    const currentStart = form.getValues('startDate')
    const currentEnd = form.getValues('endDate')
    if (startDate && (!currentStart || currentStart?.toString() !== startDate?.toString())) {
      form.setValue('startDate', startDate)
    }
    if (endDate && (!currentEnd || currentEnd?.toString() !== endDate?.toString())) {
      form.setValue('endDate', endDate)
    }
    if (!startDate && !endDate) {
      form.setValue('startDate', null)
      form.setValue('endDate', null)
    }
  }, [startDate, endDate])

  return (
    <AppForm form={form} onSubmit={onChange}>
      <Grid container sx={{ padding: '0px', paddingRight: '8px', paddingTop: '8px' }}>
        <Grid container md={12} spacing={1} sx={{ alignItems: 'center' }}>
          <Grid item md={3}>
            <FormTextField
              name="search"
              variant="outlined"
              fullWidth
              placeholder={t('search', { ns: 'common' })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MdSearch size={20} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item md={3}>
            <FormSelectField
              label={t('locality')}
              variant="outlined"
              getOptionLabel={row => row.name}
              data={data || []}
              name={'locality'}
              multiple
            />
          </Grid>
          <Grid item md={2}>
            <FormSelectField
              variant="outlined"
              getOptionLabel={row => row.name}
              label={t('resource', { ns: 'task' })}
              data={resources?.items || []}
              name={'resource'}
              multiple
            />
          </Grid>
          <Grid item md={4}>
            <Grid container>
              <Grid item md={6}>
                <BasicDatePicker
                  disabled={isDisabled}
                  name="startDate"
                  label={t('startDate')}
                  clearable
                  allowNullable
                />
              </Grid>
              <Grid item md={6}>
                <BasicDatePicker disabled={isDisabled} name="endDate" label={t('endDate')} clearable allowNullable />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </AppForm>
  )
}

export default TasksFilter
